import { useCallback, useEffect, useRef, useState } from 'react';
import { decodeServerEnvelope, encodeKioskIssueTicket } from '../services/protoService';

const WS_BASE = import.meta.env.VITE_NETTY_WS_URL || `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.hostname}:8080`;
const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 10_000];
const TOKEN_REFRESH_INTERVAL_MS = 55 * 60 * 1000; // 55 minutes

function storageKey(shopId) { return `vq_kiosk_${shopId}`; }
function deviceIdKey(shopId) { return `vq_kiosk_device_${shopId}`; }

function getOrCreateKioskDeviceId(shopId) {
  const key = deviceIdKey(shopId);
  try {
    let id = localStorage.getItem(key);
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'kiosk-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return 'kiosk-fallback-' + Date.now();
  }
}

function loadKioskSession(shopId) {
  try {
    const raw = localStorage.getItem(storageKey(shopId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveKioskSession(shopId, session) {
  if (session) localStorage.setItem(storageKey(shopId), JSON.stringify(session));
  else localStorage.removeItem(storageKey(shopId));
}

export function useKiosk(shopId) {
  const [queues, setQueues] = useState({});  // { serviceId: entries[] }
  const [calledTicket, setCalledTicket] = useState(null);
  // Per-service "now serving" state so multi-service shops can show one
  // call-out per queue on the waiting-room display. Keyed by serviceId,
  // or '_default' for single-queue shops.
  const [calledTickets, setCalledTickets] = useState({});
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [session, setSession] = useState(() => loadKioskSession(shopId));
  const [lastIssuedTicket, setLastIssuedTicket] = useState(null);
  const [issueError, setIssueError] = useState(null);

  const wsRef = useRef(null);
  const retryRef = useRef({ count: 0, timer: null });
  const activeRef = useRef(true);
  const refreshTimerRef = useRef(null);
  const pendingIssueRef = useRef(false);
  // Ref so the retry setTimeout always calls the latest connect, even
  // when connect's closure was rebuilt because the session changed.
  const connectRef = useRef(null);

  // Defense-in-depth: earlier builds passed the admin JWT as an `_at` query
  // param on preview links. That practice is removed, but a stale bookmark or
  // browser-history entry could still carry one. Strip it from the address bar
  // on mount so it can't be shoulder-surfed off a public tablet or captured by
  // analytics/Sentry page-view URLs.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('_at')) {
        params.delete('_at');
        const qs = params.toString();
        const cleaned = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
        window.history.replaceState(null, '', cleaned);
      }
    } catch { /* ignore */ }
  }, []);

  const clearRetry = useCallback(() => {
    if (retryRef.current.timer) {
      clearTimeout(retryRef.current.timer);
      retryRef.current.timer = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!shopId || !activeRef.current) return;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setWsStatus('connecting');
    // Kiosk WebSocket connections must carry a registered device's
    // deviceId + token so Netty can validate the handshake. Without a
    // session the connection will be rejected by the server with 401.
    const sess = session;
    const params = new URLSearchParams({ shopId, role: 'kiosk' });
    if (sess?.deviceId && sess?.token) {
      params.set('deviceId', sess.deviceId);
      params.set('token', sess.token);
    }
    const url = `${WS_BASE}/queue?${params.toString()}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current.count = 0;
      setRetryAttempt(0);
      setWsStatus('connected');
    };

    ws.onmessage = async ({ data }) => {
      try {
        const env = await decodeServerEnvelope(data);
        if (env.queueUpdate) {
          const svcId = env.queueUpdate.serviceId || '_default';
          setQueues(prev => ({ ...prev, [svcId]: env.queueUpdate.entries || [] }));
        }
        if (env.callNext) {
          setCalledTicket(env.callNext);
          const svcKey = env.callNext.serviceId || '_default';
          setCalledTickets((prev) => ({ ...prev, [svcKey]: env.callNext }));
        }
        if (env.completed) {
          setCalledTicket((prev) =>
            prev?.ticketUuid === env.completed.ticketUuid ? null : prev
          );
          setCalledTickets((prev) => {
            const next = { ...prev };
            for (const key of Object.keys(next)) {
              if (next[key]?.ticketUuid === env.completed.ticketUuid) delete next[key];
            }
            return next;
          });
        }
        if (env.kioskTicketIssued) {
          pendingIssueRef.current = false;
          setLastIssuedTicket({
            ticketUuid: env.kioskTicketIssued.ticketUuid,
            ticketNumber: env.kioskTicketIssued.ticketNumber,
            shopId: env.kioskTicketIssued.shopId,
            serviceId: env.kioskTicketIssued.serviceId,
            issuedAtMs: env.kioskTicketIssued.issuedAtMs,
          });
          setIssueError(null);
        }
        if (env.error && pendingIssueRef.current) {
          pendingIssueRef.current = false;
          setIssueError(env.error.errorCode || 'KIOSK_ISSUE_FAILED');
        }
      } catch (e) {
        console.error('Kiosk: failed to decode envelope', e);
      }
    };

    ws.onclose = (ev) => {
      if (!activeRef.current) return;
      setWsStatus('disconnected');
      pendingIssueRef.current = false;
      // If the server rejected with 401 (invalid/expired kiosk token),
      // clear the stale session so autoRegisterIfAdmin can retry.
      if (ev.code === 1008 || ev.code === 4001 || ev.code === 1006) {
        if (session?.deviceId) {
          console.warn('[Kiosk] WS rejected (code', ev.code, ') — clearing stale session');
          setSession(null);
          saveKioskSession(shopId, null);
        }
      }
      // Jitter ±40%: prevents lock-step reconnects after a backend restart.
      const base = RETRY_DELAYS_MS[Math.min(retryRef.current.count, RETRY_DELAYS_MS.length - 1)];
      const delay = base * (0.6 + Math.random() * 0.8);
      retryRef.current.count++;
      setRetryAttempt(retryRef.current.count);
      retryRef.current.timer = setTimeout(() => connectRef.current?.(), delay);
    };

    ws.onerror = () => {};
  }, [shopId, session]);

  // Keep the ref pointing at the current connect so retry timers fire
  // the latest closure (which has the current session).
  useEffect(() => { connectRef.current = connect; }, [connect]);

  // Token refresh loop
  useEffect(() => {
    if (!session) return;
    const refresh = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/kiosk/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deviceId: session.deviceId, currentToken: session.token }),
        });
        if (res.ok) {
          const data = await res.json();
          const updated = { ...session, token: data.token };
          setSession(updated);
          saveKioskSession(shopId, updated);
        }
      } catch (e) {
        console.error('Kiosk token refresh failed', e);
      }
    };
    refreshTimerRef.current = setInterval(refresh, TOKEN_REFRESH_INTERVAL_MS);
    return () => clearInterval(refreshTimerRef.current);
  }, [session, shopId]);

  useEffect(() => {
    activeRef.current = true;
    connect();

    // Snap back on connectivity / tab visibility change — kiosks on
    // spotty Wi-Fi or tablets that went to sleep should reconnect
    // instantly instead of waiting out the backoff timer.
    const forceReconnect = () => {
      if (!activeRef.current) return;
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) return;
      if (retryRef.current.timer) {
        clearTimeout(retryRef.current.timer);
        retryRef.current.timer = null;
      }
      retryRef.current.count = 0;
      setRetryAttempt(0);
      connectRef.current?.();
    };
    const onOnline = () => forceReconnect();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') forceReconnect();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      activeRef.current = false;
      clearRetry();
      clearInterval(refreshTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [connect, clearRetry]);

  const register = useCallback(async (deviceName, tokenOverride) => {
    // This requires an authenticated admin to call
    const token = tokenOverride || localStorage.getItem('vq_access_token') || sessionStorage.getItem('vq_access_token');
    const res = await fetch(`${API_BASE}/api/v1/shops/${shopId}/kiosk-devices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ deviceName }),
    });
    if (!res.ok) {
      const err = new Error('Registration failed');
      err.status = res.status;
      throw err;
    }
    const data = await res.json();
    const sess = { deviceId: data.deviceId, token: data.token };
    setSession(sess);
    saveKioskSession(shopId, sess);
    return sess;
  }, [shopId]);

  /**
   * Pair this (un-signed-in) tablet by redeeming a one-time code an admin
   * generated in their own browser. Exchanges the code for a shop-scoped
   * kiosk device token — no admin credential ever touches this device.
   */
  const redeemPairingCode = useCallback(async (code) => {
    const res = await fetch(`${API_BASE}/api/v1/kiosk/redeem-pairing-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) {
      const err = new Error('Pairing failed');
      err.status = res.status;
      throw err;
    }
    const data = await res.json();
    // Guard against a code minted for a different shop than this display.
    if (data.shopId && shopId && data.shopId !== shopId) {
      throw new Error('Pairing code is for a different office');
    }
    const sess = { deviceId: data.deviceId, token: data.token };
    setSession(sess);
    saveKioskSession(shopId, sess);
    return sess;
  }, [shopId]);

  /**
   * Best-effort auto-register for the common "an admin visits the kiosk
   * page for the first time" flow. Uses the admin's JWT from localStorage
   * to call POST /shops/:shopId/kiosk-devices. Safe to call repeatedly —
   * if a session already exists it's a no-op. Returns true if a session
   * is now available, false otherwise.
   */
  const autoRegisterIfAdmin = useCallback(async () => {
    // If we already have a session, verify it by calling the refresh endpoint.
    // If the token is invalid/expired on the server side, clear it and re-register.
    if (session?.deviceId && session?.token) {
      try {
        const verifyRes = await fetch(`${API_BASE}/api/v1/kiosk/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deviceId: session.deviceId, currentToken: session.token }),
        });
        if (verifyRes.ok) return true; // Session is valid
        // Token invalid — clear and fall through to re-register
        console.warn('[Kiosk] Existing session token rejected — clearing');
        setSession(null);
        saveKioskSession(shopId, null);
      } catch {
        // Network error — keep existing session and hope for the best
        return true;
      }
    }
    // Try to refresh the access token first (it may have expired).
    // Token may be in localStorage or sessionStorage depending on whether
    // "Remember me" was checked at login. CRITICAL: write any refreshed
    // tokens back to the SAME storage tier they came from — never force
    // them into localStorage. Doing so would silently promote a
    // session-scoped ("remember me" off) admin login into a persistent
    // credential on what may be an unattended public tablet.
    const inLocal = !!localStorage.getItem('vq_refresh_token');
    const refreshStore = inLocal ? localStorage : sessionStorage;
    const refreshToken = refreshStore.getItem('vq_refresh_token');
    if (refreshToken) {
      try {
        const refreshRes = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          refreshStore.setItem('vq_access_token', data.accessToken);
          if (data.refreshToken) refreshStore.setItem('vq_refresh_token', data.refreshToken);
        }
      } catch (e) { console.warn('[Kiosk] Token refresh failed:', e.message); }
    }
    // Use the admin JWT from storage ONLY. We never read it from the URL:
    // a bearer token in a query string leaks into logs, history and
    // analytics. Preview links no longer carry one (see SettingsPage).
    const adminJwt = localStorage.getItem('vq_access_token') || sessionStorage.getItem('vq_access_token');
    if (!adminJwt) {
      console.warn('[Kiosk] No admin session found — cannot auto-register');
      return false;
    }
    try {
      const name = `kiosk-${Math.random().toString(36).slice(2, 8)}`;
      await register(name, adminJwt);
      console.info('[Kiosk] Auto-registration successful');
      return true;
    } catch (e) {
      console.error('[Kiosk] Auto-registration failed:', e.message, e.status ? `HTTP ${e.status}` : '');
      return false;
    }
  }, [session, register]);

  const issueWalkUpTicket = useCallback(async ({ serviceId = '' } = {}) => {
    if (!shopId) throw new Error('shopId missing');
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      throw new Error('Kiosk WebSocket not connected');
    }
    if (pendingIssueRef.current) {
      throw new Error('Ticket issuance already in progress');
    }
    pendingIssueRef.current = true;
    setIssueError(null);
    const requestId = `kiosk-issue-${Date.now()}`;
    const kioskDeviceId = getOrCreateKioskDeviceId(shopId);
    const payload = await encodeKioskIssueTicket({ requestId, shopId, serviceId, kioskDeviceId });
    ws.send(payload);
  }, [shopId]);

  const clearLastIssuedTicket = useCallback(() => {
    setLastIssuedTicket(null);
    setIssueError(null);
  }, []);

  const allEntries = Object.values(queues).flat();

  return {
    queues,
    entries: allEntries,
    calledTicket,
    calledTickets,
    wsStatus,
    retryAttempt,
    session,
    register,
    autoRegisterIfAdmin,
    redeemPairingCode,
    issueWalkUpTicket,
    lastIssuedTicket,
    issueError,
    clearLastIssuedTicket,
  };
}
